import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Brand } from "@/components/Brand";
import RentFundView, { RentFundStatus } from "@/components/RentFundView";
import { getPublicRentFund } from "@/lib/api";

// Public, read-only rent fund page for investors: /investor/:token. No login.
const REFRESH_MS = 60_000;

export default function InvestorRentFund() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loadedAt, setLoadedAt] = useState(null);

  const load = useCallback(async () => {
    try {
      setData(await getPublicRentFund(token));
      setError(null);
      setLoadedAt(new Date());
    } catch (err) {
      const code = err?.response?.status;
      setError(code === 404 ? "This link isn't valid or has been turned off. Ask Liftr for a new one." : "Couldn't load the latest figures. Retrying in a minute.");
    }
  }, [token]);

  useEffect(() => {
    document.title = "Liftr · Rent fund";
    load();
    const t = setInterval(() => { if (document.visibilityState === "visible") load(); }, REFRESH_MS);
    return () => clearInterval(t);
  }, [load]);

  const f = data?.facility;
  return (
    <div className="min-h-screen px-4 sm:px-8 py-8 max-w-6xl mx-auto space-y-8" data-testid="investor-page">
      <header className="flex items-center justify-between flex-wrap gap-4">
        <Brand size={36} />
        {loadedAt && <span className="text-xs text-zinc-500">Live from FPA · updated {loadedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>}
      </header>

      {error && !data && <div className="card-surface p-6 text-sm text-zinc-300">{error}</div>}
      {!data && !error && <div className="card-surface p-6 text-sm text-zinc-500">Loading…</div>}

      {data && (
        <>
          <div className="flex items-end justify-between flex-wrap gap-3">
            <div>
              <div className="eyebrow">{[f.name, f.city].filter(Boolean).join(" · ")} · investor view</div>
              <h1 className="font-display text-4xl font-extrabold mt-1 tracking-tight">Rent fund</h1>
              <p className="text-sm text-zinc-400 mt-1 max-w-2xl">
                Weekly net revenue against the amount needed to pay the gym's rent by its deadline while covering salaries,
                advertising and utilities. Figures come straight from the gym's sales records.
              </p>
            </div>
            <RentFundStatus status={data.status} />
          </div>
          {error && <div className="text-xs text-amber-300">{error}</div>}
          <RentFundView data={data} />
        </>
      )}
    </div>
  );
}
